import React, { useState } from "react";
import { api } from "./api";

// Geocode using Open-Meteo's free geocoding API
// Extracts city from full address for better results
async function geocodeAddress(address) {
  // Extract city — try "City, State" pattern first, then just use full address
  const cityMatch = address.match(/([^,]+),\s*([A-Z]{2})/);
  const query = cityMatch ? `${cityMatch[1].trim()} ${cityMatch[2]}` : address;

  const res = await fetch(
    `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(query)}&count=5&language=en&format=json&countryCode=US`
  );
  const data = await res.json();
  if (data.results && data.results.length > 0) {
    const r = data.results[0];
    return { lat: r.latitude, lon: r.longitude };
  }

  // Fallback: try just the city name alone
  if (cityMatch) {
    const res2 = await fetch(
      `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(cityMatch[1].trim())}&count=5&language=en&format=json&countryCode=US`
    );
    const data2 = await res2.json();
    if (data2.results && data2.results.length > 0) {
      return { lat: data2.results[0].latitude, lon: data2.results[0].longitude };
    }
  }

  return null;
}

export default function SettingsModal({ settings, onClose, onSave }) {
  const [address,    setAddress]    = useState(settings.address || "");
  const [saving,     setSaving]     = useState(false);
  const [uploading,  setUploading]  = useState(false);
  const [error,      setError]      = useState(null);
  const [photoPreview, setPhotoPreview] = useState(settings.photo_url || null);
  const [uploadStatus, setUploadStatus] = useState(null);

  async function handleSave(e) {
    e.preventDefault();
    setError(null);
    setSaving(true);

    try {
      // Geocode address using Open-Meteo (free, no key)
      let lat = settings.lat;
      let lon = settings.lon;

      if (address && address !== settings.address) {
        const coords = await geocodeAddress(address);
        if (coords) {
          lat = coords.lat;
          lon = coords.lon;
        }
        // If geocoding fails, still save the address — weather uses last known coords
      }

      await api.putSettings({ address, lat, lon });
      onSave({ ...settings, address, lat, lon });
      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  async function handlePhotoChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select an image file.");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError("Photo must be under 10MB.");
      return;
    }

    setUploading(true);
    setError(null);
    setUploadStatus("Getting upload URL…");

    try {
      // 1. Get presigned upload URL from Lambda — pass the file's real
      // content-type so the stored object (extension + Content-Type) matches
      // what's actually being uploaded, instead of always being labeled jpg.
      const { upload_url } = await api.getPhotoUploadUrl(file.type);

      setUploadStatus("Uploading photo…");

      // 2. PUT directly to S3 (no Lambda in the path). Must match the
      // content-type the presigned URL was signed with, above.
      const uploadRes = await fetch(upload_url, {
        method:  "PUT",
        headers: { "Content-Type": file.type },
        body:    file,
      });

      if (!uploadRes.ok) throw new Error("Upload failed");

      // 3. Show preview
      const reader = new FileReader();
      reader.onload = ev => setPhotoPreview(ev.target.result);
      reader.readAsDataURL(file);

      setUploadStatus("✓ Photo uploaded");
      setTimeout(() => setUploadStatus(null), 3000);

    } catch (err) {
      setError(err.message);
      setUploadStatus(null);
    } finally {
      setUploading(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <h2>⚙️ Property Settings</h2>
          <button className="btn-icon" onClick={onClose}>✕</button>
        </div>

        <form className="modal-form" onSubmit={handleSave}>
          {error && <div className="error-banner">⚠️ {error}</div>}

          {/* House photo */}
          <div className="photo-upload-section">
            <label className="photo-upload-label">House Photo</label>
            <div className="photo-upload-area">
              {photoPreview ? (
                <img src={photoPreview} alt="House" className="photo-preview" />
              ) : (
                <div className="photo-placeholder">🏠<br/>No photo yet</div>
              )}
              <div className="photo-upload-controls">
                <label className="btn btn-ghost photo-file-btn">
                  {uploading ? "Uploading…" : "Choose Photo"}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoChange}
                    disabled={uploading}
                    style={{ display: "none" }}
                  />
                </label>
                {uploadStatus && <span className="upload-status">{uploadStatus}</span>}
                <p className="photo-hint">JPG, PNG, HEIC — max 10MB</p>
              </div>
            </div>
          </div>

          {/* Address */}
          <label>
            Property Address
            <input
              type="text"
              value={address}
              onChange={e => setAddress(e.target.value)}
              placeholder="e.g. Atlanta, GA  (used for weather)"
            />
            <span className="field-hint">Enter city name for weather (e.g. Atlanta, GA)</span>
          </label>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? "Saving…" : "Save Settings"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
